// Types for the roguelike run ("Operation") system. A run is a seeded DAG of
// mission nodes; the player fights column by column toward the final node.
// Squad survivors, drafted field mods, and earned ops all live on the run
// and die with it.

// Theaters the campaign can deploy to (mission/variants.ts THEATERS carries
// the content; this union is the persisted key).
export type RunTheater = "stone-wall" | "iron-market" | "green-hammer" | "iron-curtain" | "arctic-wolf";

export type NodeKind =
  | "combat" // escort segment
  | "elite" // hardened escort segment
  | "defend" // hold a strongpoint against waves
  | "destroy" // squad-less fire mission, timed
  | "ambush" // squad-less convoy intercept
  | "delay" // hold a chokepoint; a column converges — none may pass
  | "sead" // squad-less: silence every AA site before the window closes
  | "extraction" // squad pinned deep — clear the lane, send them with H
  | "rescue" // two elements: reach the trapped one, then bring everyone home
  | "resupply"
  | "final";

// Mission modifiers (docs/design/MISSION_VARIETY.md §Mission Modifiers):
// seeded onto nodes like night, each with a real engine or builder effect.
export type NodeModifier = "storm" | "escalating" | "aa-dense" | "heavy-armor";

export type RunNode = {
  id: string;
  kind: NodeKind;
  col: number; // 0..COLUMNS-1, left to right
  row: number; // vertical slot within the column, for layout
  // Which mission variant template this node uses (index into the valley
  // template list, chosen at generation so the map shows what you're facing).
  variant: number;
  // Pre-dawn sortie: mission flies with the thermal sensor as default.
  night?: boolean;
  // At most one mission modifier per node (storm / escalating / aa-dense /
  // heavy-armor) — rolled at generation like night.
  modifier?: NodeModifier;
  next: string[]; // node ids in col+1 reachable from here
};

export type RunState = {
  seed: number;
  theater: RunTheater;
  nodes: RunNode[];
  // Node the squad currently sits on; null before the first pick (any col-0
  // node is available).
  currentNodeId: string | null;
  clearedNodeIds: string[];
  squadSize: number;
  squadMax: number;
  // Alive operators by name, length === squadSize. Names carry between
  // missions; losses are people, not numbers (meta/operators.ts).
  roster: string[];
  // Operators lost this run — never redrawn as replacements, listed on the
  // run-over memorial. "They go home or they don't."
  fallen: string[];
  // Of the fallen: those lost in the run-ending squad wipe. Wipe losses are
  // KIA on the profile; earlier losses come home wounded.
  wipeLosses: string[];
  // Ready operators waiting at the FOB — resupply draws from here first.
  reserves: string[];
  modIds: string[]; // drafted field mods, in draft order
  opsEarned: number; // banked to the profile when the run ends
  kills: number;
  // Effective REDFOR pressure this Operation deployed into: the theater's
  // escalation-while-ignored plus its NG+ floor, frozen at creation
  // (meta/campaign.ts). Folds into per-node difficulty scaling.
  escalation: number;
  // Set when the run has ended, one way or the other.
  outcome: "active" | "victory" | "defeat";
};

export const RUN_COLUMNS = 5;
export const BASE_SQUAD_SIZE = 4;
